import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Plus, Users } from 'lucide-react';
import { format } from 'date-fns';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Drawer,
  Skeleton,
  StatusPill,
  Table,
  TextArea,
  useToast,
  ClientContacts,
} from '../components';
import { PageHeader } from '../layouts/PageHeader';
import { useT } from '../i18n';
import type { Booking, Client } from '../types/api';
import styles from './Clients.module.css';

interface ClientFormPayload {
  companyName: string;
  inn?: string;
  website?: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  industry?: string;
  notes?: string;
}

const emptyForm: ClientFormPayload = {
  companyName: '',
  inn: '',
  website: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  industry: '',
  notes: '',
};

/** Strip empty optionals before sending. */
function cleanPayload(payload: ClientFormPayload): ClientFormPayload {
  const clean: ClientFormPayload = {
    companyName: payload.companyName.trim(),
    contactName: payload.contactName.trim(),
    contactEmail: payload.contactEmail.trim(),
    contactPhone: payload.contactPhone.trim(),
  };
  if (payload.inn?.trim())      clean.inn      = payload.inn.trim();
  if (payload.website?.trim())  clean.website  = payload.website.trim();
  if (payload.industry?.trim()) clean.industry = payload.industry.trim();
  if (payload.notes?.trim())    clean.notes    = payload.notes.trim();
  return clean;
}

export function ClientsPage() {
  const { user } = useAuth();
  const { t } = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isManufacturer = user?.role === 'MANUFACTURER';

  // ── Data ─────────────────────────────────────────────────────────
  const clientsQ = useQuery<Client[]>({
    queryKey: ['clients'],
    queryFn: () => api.get<Client[]>('/clients'),
  });
  const bookingsQ = useQuery<Booking[]>({
    queryKey: ['bookings'],
    queryFn: () => api.get<Booking[]>('/bookings'),
  });

  const clients = clientsQ.data ?? [];
  const bookings = bookingsQ.data ?? [];

  // ── Create flow ──────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<ClientFormPayload>(emptyForm);
  const updateField = <K extends keyof ClientFormPayload>(k: K, v: ClientFormPayload[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const createMutation = useMutation({
    mutationFn: (payload: ClientFormPayload) => api.post<Client>('/clients', cleanPayload(payload)),
    onSuccess: (newClient) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success(t('clients.toastCreated'), newClient.companyName);
      setCreateOpen(false);
      setForm(emptyForm);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('clients.errorCreate');
      toast.error(t('clients.toastCreateFail'), msg);
    },
  });

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (createMutation.isPending) return;
    createMutation.mutate(form);
  };

  const requiredOk =
    form.companyName.trim() &&
    form.contactName.trim() &&
    form.contactEmail.trim() &&
    form.contactPhone.trim();

  // ── Edit flow ──────────────────────────────────────────────────
  const [editClient, setEditClient] = useState<Client | null>(null);
  const [editForm, setEditForm] = useState<ClientFormPayload>(emptyForm);
  const updateEditField = <K extends keyof ClientFormPayload>(k: K, v: ClientFormPayload[K]) =>
    setEditForm((prev) => ({ ...prev, [k]: v }));

  const openEdit = (c: Client) => {
    updateMutation.reset();
    setEditForm({
      companyName: c.companyName,
      inn: c.inn ?? '',
      website: c.website ?? '',
      contactName: c.contactName,
      contactEmail: c.contactEmail,
      contactPhone: c.contactPhone,
      industry: c.industry ?? '',
      notes: c.notes ?? '',
    });
    setEditClient(c);
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ClientFormPayload }) =>
      api.put<Client>(`/clients/${id}`, cleanPayload(payload)),
    onSuccess: (updated) => {
      // Issue #64 — the client name shows up on leads/kanban and in report
      // drill-ins: refresh every view that embeds the client, not just the list.
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success(t('clients.toastUpdated'), updated.companyName);
      setEditClient(null);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('clients.errorUpdate');
      toast.error(t('clients.toastUpdateFail'), msg);
    },
  });

  const submitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (updateMutation.isPending || !editClient) return;
    updateMutation.mutate({ id: editClient.id, payload: editForm });
  };

  // Issue #64 — the API requires only the company name (contacts are optional
  // since #59), so a client created with just a name stays editable.
  const editRequiredOk = editForm.companyName.trim();

  // Issue #61/#64 — a rejected save is surfaced inline in the edit form.
  const editErrorMsg =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : updateMutation.error
        ? t('clients.errorUpdate')
        : null;

  // ── Detail drawer (supports ?id= deep-link from EntityLink) ──────
  const [searchParams, setSearchParams] = useSearchParams();
  const idFromUrl = searchParams.get('id');
  const [selectedId, setSelectedId] = useState<string | null>(idFromUrl);

  useEffect(() => {
    setSelectedId(idFromUrl);
  }, [idFromUrl]);

  const openClient = (id: string) => {
    setSelectedId(id);
    setSearchParams({ id }, { replace: true });
  };

  const closeClient = () => {
    setSelectedId(null);
    if (searchParams.has('id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('id');
      setSearchParams(next, { replace: true });
    }
  };

  const selectedClient = selectedId ? clients.find((c) => c.id === selectedId) ?? null : null;
  const clientBookings = (cid: string) => bookings.filter((b) => b.clientId === cid);

  // ── Render ───────────────────────────────────────────────────────
  const isLoading = clientsQ.isLoading || bookingsQ.isLoading;
  const errorMsg = clientsQ.error instanceof Error ? clientsQ.error.message : null;

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('clients.title')}
        description={isManufacturer ? t('clients.descAll') : t('clients.descOwn')}
        action={
          isManufacturer && (
            <Button leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
              {t('clients.add')}
            </Button>
          )
        }
      />

      {errorMsg && (
        <Card className={styles.error}>
          <p>{errorMsg}</p>
        </Card>
      )}

      {isLoading ? (
        <ClientsTableSkeleton />
      ) : clients.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users size={28} />}
            title={t('clients.emptyTitle')}
            description={
              isManufacturer
                ? t('clients.emptyDescManufacturer')
                : t('clients.emptyDescIntegrator')
            }
            action={
              isManufacturer && (
                <Button leftIcon={<Plus size={16} />} size="sm" onClick={() => setCreateOpen(true)}>
                  {t('clients.add')}
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t('clients.colCompany')}</th>
              <th>{t('clients.colInn')}</th>
              <th>{t('clients.colContact')}</th>
              <th>{t('clients.colEmail')}</th>
              <th>{t('clients.colPhone')}</th>
              <th>{t('clients.colIndustry')}</th>
              <th>{t('clients.colBooking')}</th>
              <th className="actions">{/* row actions */}</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => {
              const myBookings = clientBookings(c.id);
              const activeBooking = myBookings.find((b) => b.active);
              return (
                <tr key={c.id}>
                  <td>
                    <button
                      type="button"
                      className={styles.companyLink}
                      onClick={() => openClient(c.id)}
                    >
                      {c.companyName}
                    </button>
                  </td>
                  <td>{c.inn ?? <span className={styles.muted}>—</span>}</td>
                  <td>{c.contactName}</td>
                  <td>{c.contactEmail}</td>
                  <td>{c.contactPhone}</td>
                  <td>{c.industry ?? <span className={styles.muted}>—</span>}</td>
                  <td>
                    {activeBooking ? (
                      <StatusPill tone="info">
                        {activeBooking.integrator?.name ?? '—'}
                      </StatusPill>
                    ) : (
                      <span className={styles.muted}>{t('common.none')}</span>
                    )}
                  </td>
                  <td className="actions">
                    <Button variant="secondary" size="sm" onClick={() => openClient(c.id)}>
                      {t('common.details')}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* ── Create modal ────────────────────────────────────────── */}
      <Modal
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        title={t('clients.modalTitle')}
        description={t('clients.modalDesc')}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setCreateOpen(false)}
              disabled={createMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="create-client-form"
              loading={createMutation.isPending}
              disabled={!requiredOk}
            >
              {t('common.create')}
            </Button>
          </>
        }
      >
        <form id="create-client-form" onSubmit={submitCreate} className={styles.form} noValidate>
          <Input
            label={t('clients.fieldCompanyName')}
            value={form.companyName}
            onChange={(e) => updateField('companyName', e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <Input
            label={t('clients.fieldInn')}
            value={form.inn ?? ''}
            onChange={(e) => updateField('inn', e.target.value)}
            fullWidth
          />
          <Input
            label={t('clients.fieldWebsite')}
            value={form.website ?? ''}
            onChange={(e) => updateField('website', e.target.value)}
            fullWidth
          />
          <Input
            label={t('clients.fieldContactName')}
            value={form.contactName}
            onChange={(e) => updateField('contactName', e.target.value)}
            required
            fullWidth
          />
          <Input
            label={t('clients.fieldContactEmail')}
            type="email"
            value={form.contactEmail}
            onChange={(e) => updateField('contactEmail', e.target.value)}
            required
            fullWidth
          />
          <Input
            label={t('clients.fieldContactPhone')}
            value={form.contactPhone}
            onChange={(e) => updateField('contactPhone', e.target.value)}
            required
            fullWidth
          />
          <Input
            label={t('clients.fieldIndustry')}
            value={form.industry ?? ''}
            onChange={(e) => updateField('industry', e.target.value)}
            fullWidth
          />
          <TextArea
            label={t('clients.fieldNotes')}
            value={form.notes ?? ''}
            onChange={(e) => updateField('notes', e.target.value)}
            fullWidth
            rows={3}
          />
        </form>
      </Modal>

      {/* ── Edit modal ──────────────────────────────────────────── */}
      <Modal
        open={Boolean(editClient)}
        onClose={() => !updateMutation.isPending && setEditClient(null)}
        title={t('clients.editModalTitle')}
        description={t('clients.editModalDesc')}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setEditClient(null)}
              disabled={updateMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="edit-client-form"
              loading={updateMutation.isPending}
              disabled={!editRequiredOk}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {editErrorMsg && (
          <p className={styles.formError} role="alert">
            {editErrorMsg}
          </p>
        )}
        <form id="edit-client-form" onSubmit={submitEdit} className={styles.form} noValidate>
          <Input
            label={t('clients.fieldCompanyName')}
            value={editForm.companyName}
            onChange={(e) => updateEditField('companyName', e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <Input
            label={t('clients.fieldInn')}
            value={editForm.inn ?? ''}
            onChange={(e) => updateEditField('inn', e.target.value)}
            fullWidth
          />
          <Input
            label={t('clients.fieldWebsite')}
            value={editForm.website ?? ''}
            onChange={(e) => updateEditField('website', e.target.value)}
            fullWidth
          />
          <Input
            label={t('clients.fieldContactName')}
            value={editForm.contactName}
            onChange={(e) => updateEditField('contactName', e.target.value)}
            fullWidth
          />
          <Input
            label={t('clients.fieldContactEmail')}
            type="email"
            value={editForm.contactEmail}
            onChange={(e) => updateEditField('contactEmail', e.target.value)}
            fullWidth
          />
          <Input
            label={t('clients.fieldContactPhone')}
            value={editForm.contactPhone}
            onChange={(e) => updateEditField('contactPhone', e.target.value)}
            fullWidth
          />
          <Input
            label={t('clients.fieldIndustry')}
            value={editForm.industry ?? ''}
            onChange={(e) => updateEditField('industry', e.target.value)}
            fullWidth
          />
          <TextArea
            label={t('clients.fieldNotes')}
            value={editForm.notes ?? ''}
            onChange={(e) => updateEditField('notes', e.target.value)}
            fullWidth
            rows={3}
          />
        </form>
      </Modal>

      {/* ── Detail drawer ───────────────────────────────────────── */}
      <Drawer
        open={Boolean(selectedClient)}
        onClose={closeClient}
        title={selectedClient?.companyName ?? ''}
        description={selectedClient?.industry ?? undefined}
        size="md"
        footer={
          <>
            {isManufacturer && selectedClient && (
              <Button variant="secondary" onClick={() => openEdit(selectedClient)}>
                {t('clients.edit')}
              </Button>
            )}
            <Button variant="secondary" onClick={closeClient}>
              {t('common.close')}
            </Button>
          </>
        }
      >
        {selectedClient && (
          <ClientDetail client={selectedClient} bookings={clientBookings(selectedClient.id)} />
        )}
      </Drawer>
    </div>
  );
}

// ── Detail content ────────────────────────────────────────────────

function ClientDetail({ client, bookings }: { client: Client; bookings: Booking[] }) {
  const { t } = useT();
  const { user } = useAuth();
  const isManufacturer = user?.role === 'MANUFACTURER';
  return (
    <div className={styles.detail}>
      <section className={styles.detailGrid}>
        <DetailField label={t('clients.colInn')} value={client.inn ?? '—'} />
        <DetailField
          label={t('clients.fieldWebsite')}
          value={
            client.website ? (
              <a href={client.website} target="_blank" rel="noreferrer">
                {client.website}
              </a>
            ) : (
              '—'
            )
          }
        />
        <DetailField label={t('clients.colContact')} value={client.contactName} />
        <DetailField label={t('clients.colEmail')} value={client.contactEmail} />
        <DetailField label={t('clients.colPhone')} value={client.contactPhone} />
        <DetailField label={t('clients.fieldIndustry')} value={client.industry ?? '—'} />
        <DetailField label={t('clients.fieldNotes')} value={client.notes ?? '—'} span={2} />
      </section>

      {/* Issue #54 — contacts of the client (primary flagged). Editable for
          the manufacturer; legacy single-contact fields above mirror it. */}
      <ClientContacts clientId={client.id} readOnly={!isManufacturer} />

      <section className={styles.detailSection}>
        <h3 className={styles.detailHeading}>{t('clients.bookingHistory')}</h3>
        {bookings.length === 0 ? (
          <p className={styles.muted}>{t('clients.noBookings')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t('clients.colIntegrator')}</th>
                <th>{t('clients.colStatus')}</th>
                <th>{t('clients.colDate')}</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id}>
                  <td>{b.integrator?.name ?? '—'}</td>
                  <td>
                    <StatusPill tone={b.active ? 'success' : 'neutral'} subtle={!b.active}>
                      {b.active ? t('common.active') : t('common.inactive')}
                    </StatusPill>
                  </td>
                  <td>{format(new Date(b.bookedAt), 'yyyy-MM-dd')}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>
    </div>
  );
}

function DetailField({
  label,
  value,
  span,
}: {
  label: string;
  value: React.ReactNode;
  span?: number;
}) {
  return (
    <div className={styles.detailField} style={span === 2 ? { gridColumn: '1 / -1' } : undefined}>
      <div className={styles.detailLabel}>{label}</div>
      <div className={styles.detailValue}>{value}</div>
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────

function ClientsTableSkeleton() {
  const { t } = useT();
  return (
    <Table>
      <thead>
        <tr>
          <th>{t('clients.colCompany')}</th>
          <th>{t('clients.colInn')}</th>
          <th>{t('clients.colContact')}</th>
          <th>{t('clients.colEmail')}</th>
          <th>{t('clients.colPhone')}</th>
          <th>{t('clients.colIndustry')}</th>
          <th>{t('clients.colBooking')}</th>
          <th className="actions" />
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 5 }).map((_, i) => (
          <tr key={i}>
            {Array.from({ length: 7 }).map((__, j) => (
              <td key={j}><Skeleton width="80%" /></td>
            ))}
            <td className="actions"><Skeleton width={56} /></td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
